import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  defaultFills,
  Icon,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  Popover,
  Spinner,
} from '.';
import { UserThumbnail } from '../components/settings/UserThumbnail';
import { ITeamMember, useStores } from '../stores';
import { IQueryCache } from '../stores/utils/modelWithCRUD';

export const ResponsibleFilter = ({
  onSelect,
}: {
  onSelect: (members: ITeamMember[]) => void;
}) => {
  const intl = useIntl();
  const { teamMemberStore, authStore } = useStores();

  const [cache, setCache] = useState<IQueryCache<ITeamMember>>();

  useEffect(() => {
    let isSub = true;
    teamMemberStore.getAll().then((members) => isSub && setCache(members));
    return () => {
      isSub = false;
    };
  }, [teamMemberStore]);

  const meMember = cache
    ? cache.items.find((m) => m.user.id === authStore.me!.id)
    : undefined;

  const [selectedItems, setSelectedItems] = useState<ITeamMember[]>([]);

  useEffect(() => {
    onSelect(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems]);

  const toogleItem = (member: ITeamMember) => {
    setSelectedItems((items) => {
      const index = items.indexOf(member);
      if (index > -1) {
        const newItems = [...items];
        newItems.splice(index, 1);
        return newItems;
      } else {
        return [...items, member];
      }
    });
  };

  return (
    <Popover
      placement="bottom-end"
      content={(close) => (
        <Menu>
          <MenuDivider
            text={intl.formatMessage({
              defaultMessage: 'Filter by responsible',
            })}
          />
          <MenuItem
            onClick={() => {
              setSelectedItems([]);
              close();
            }}
            text={
              <Box
                display="flex"
                alignItems="center"
                className={classNames(selectedItems.length === 0 && 'medium')}
              >
                <Icon
                  icon={selectedItems.length === 0 ? 'small-tick' : 'blank'}
                  color={defaultFills.info[500]}
                  marginRight={majorScale(1)}
                />
                <FormattedMessage
                  defaultMessage="Everyone"
                  description="Select responsible button"
                />
              </Box>
            }
          />
          {meMember && (
            <MenuItem
              onClick={() => {
                toogleItem(meMember);
              }}
              text={
                <Box
                  display="flex"
                  alignItems="center"
                  className={classNames(
                    selectedItems.indexOf(meMember) > -1 && 'medium'
                  )}
                >
                  <Icon
                    icon={
                      selectedItems.indexOf(meMember) > -1
                        ? 'small-tick'
                        : 'blank'
                    }
                    color={defaultFills.info[500]}
                    marginRight={majorScale(1)}
                  />
                  <UserThumbnail user={meMember.user} showName />
                </Box>
              }
            />
          )}
          <MenuDivider />
          {cache && !cache.isLoading ? (
            cache.items
              .filter((m) => m.user.id !== authStore.me!.id)
              .map((member) => (
                <MenuItem
                  key={member.id}
                  onClick={() => {
                    toogleItem(member);
                  }}
                  text={
                    <Box
                      display="flex"
                      alignItems="center"
                      className={classNames(
                        selectedItems.indexOf(member) > -1 && 'medium'
                      )}
                    >
                      <Icon
                        icon={
                          selectedItems.indexOf(member) > -1
                            ? 'small-tick'
                            : 'blank'
                        }
                        color={defaultFills.info[500]}
                        marginRight={majorScale(1)}
                      />
                      <UserThumbnail user={member.user} showName />
                    </Box>
                  }
                />
              ))
          ) : (
            <Spinner size={16} />
          )}
        </Menu>
      )}
    >
      <Button
        iconLeft="filter"
        iconRight="caret-down"
        marginRight={majorScale(1)}
      >
        {selectedItems.length === 0 ? (
          <FormattedMessage
            defaultMessage="Everyone"
            description="Responsible is 'Everyone'"
          />
        ) : (
          <FormattedMessage
            defaultMessage="{name} {members, plural, =0 {} other {and # more}}"
            values={{
              name: selectedItems[0].user.name,
              members: selectedItems.length - 1,
            }}
          />
        )}
      </Button>
    </Popover>
  );
};
